// Lease economics: turns lease inputs into a month-by-month rent schedule and the
// totals commissions are calculated on (Section 5.1 of the spec).

import { CommissionInputError } from './errors';

export type RentMode = 'psf_year' | 'monthly';

export type Escalation =
  | { kind: 'none' }
  /** Annual % increase applied on each lease anniversary. */
  | { kind: 'percent'; annualPct: number }
  /** Fixed $/SF/yr bump applied on each lease anniversary. */
  | { kind: 'fixed_psf'; annualPsf: number }
  /** Year-by-year rent; values[0] is year 1. Entered in the same mode as `mode`. */
  | { kind: 'custom'; mode: RentMode; values: number[] }
  /**
   * Rent steps over month ranges (a ramp), then an optional annual % increase every
   * 12 months after the last step ends. Months before the first step use the base rent;
   * a gap between steps keeps the previous step's rent.
   */
  | { kind: 'steps'; steps: RentStep[]; thereafterPct?: number | null };

export interface RentStep {
  /** 1-based, inclusive. */
  fromMonth: number;
  toMonth: number;
  /** Monthly dollars, $/SF/yr, or a % increase over the rent just before this step. */
  unit: RentMode | 'pct_increase';
  value: number;
}

export interface Abatement {
  /** First abated month, 1-based (month 1 = first month of the term). */
  startMonth: number;
  months: number;
  /** 100 = full free rent, 50 = half rent. */
  pct: number;
}

export interface LeaseInput {
  rentableSf: number;
  termMonths: number;
  /** Starting base rent, entered either as $/SF/yr or as total monthly dollars. */
  baseRent: { mode: RentMode; value: number };
  escalation?: Escalation;
  abatements?: Abatement[];
  /** Paid on OPEX. OPEX is flat for the whole term: no escalation, no true-up. */
  opex?: { enabled: boolean; psfYear?: number | null };
}

export interface LeaseMonth {
  month: number;
  year: number;
  baseRent: number;
  abatedPct: number;
  abatedAmount: number;
}

export interface LeaseAnalysis {
  rentableSf: number;
  termMonths: number;
  /** Starting monthly base rent in dollars (derived when rent was entered as $/SF/yr). */
  startingMonthlyRent: number;
  /** Starting base rent in $/SF/yr (derived when rent was entered monthly). Null when SF is 0. */
  startingRentPsfYear: number | null;
  aggregateBaseRentUnabated: number;
  abatedRent: number;
  /** Escalated base rent over the term, minus abated rent. */
  aggregateBaseRent: number;
  aggregateOpex: number;
  aggregateLeaseValue: number;
  /** First month with no abatement at all; null if every month is abated. */
  firstFullMonth: number | null;
  firstFullMonthBaseRent: number | null;
  monthlyOpex: number;
  /** First full unabated month's base rent, plus monthly OPEX when paid on OPEX. */
  commissionMonthlyRent: number | null;
  opexEnabled: boolean;
  schedule: LeaseMonth[];
}

function requirePositive(value: number, field: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new CommissionInputError(`${field} must be greater than zero (got ${value}).`);
  }
}

function toMonthly(mode: RentMode, value: number, sf: number): number {
  if (mode === 'monthly') return value;
  requirePositive(sf, 'Rentable SF');
  return (value * sf) / 12;
}

/** Monthly base rent in effect during lease year `year` (1-based), before abatement. */
export function monthlyBaseRentForYear(input: LeaseInput, year: number): number {
  const sf = input.rentableSf;
  const start = toMonthly(input.baseRent.mode, input.baseRent.value, sf);
  const esc = input.escalation ?? { kind: 'none' };
  switch (esc.kind) {
    case 'none':
      return start;
    case 'percent':
      return start * Math.pow(1 + esc.annualPct / 100, year - 1);
    case 'fixed_psf':
      requirePositive(sf, 'Rentable SF');
      return start + ((esc.annualPsf * sf) / 12) * (year - 1);
    case 'custom': {
      const value = esc.values[year - 1];
      if (value === undefined) {
        throw new CommissionInputError(
          `The custom rent schedule has no rent for lease year ${year}. Add a row for every year of the term.`,
        );
      }
      return toMonthly(esc.mode, value, sf);
    }
    case 'steps':
      return monthlyBaseRent(input, (year - 1) * 12 + 1);
  }
}

function validateSteps(steps: RentStep[]): void {
  let last = 0;
  for (const st of steps) {
    if (!Number.isInteger(st.fromMonth) || !Number.isInteger(st.toMonth) || st.fromMonth < 1 || st.toMonth < st.fromMonth) {
      throw new CommissionInputError('Each rent step needs a start and end month (whole numbers, end on or after start).');
    }
    if (st.fromMonth <= last) throw new CommissionInputError(`Rent steps overlap or are out of order at month ${st.fromMonth}.`);
    if (!Number.isFinite(st.value) || (st.unit === 'pct_increase' ? st.value < 0 : st.value <= 0)) {
      throw new CommissionInputError(`The rent step starting month ${st.fromMonth} needs an amount.`);
    }
    last = st.toMonth;
  }
}

/** Monthly base rent in effect in `month` (1-based), before abatement. */
export function monthlyBaseRent(input: LeaseInput, month: number): number {
  const esc = input.escalation ?? { kind: 'none' };
  if (esc.kind !== 'steps') return monthlyBaseRentForYear(input, Math.ceil(month / 12));
  validateSteps(esc.steps);
  let rent = toMonthly(input.baseRent.mode, input.baseRent.value, input.rentableSf);
  for (const st of esc.steps) {
    if (st.fromMonth > month) return rent;
    rent = st.unit === 'pct_increase' ? rent * (1 + st.value / 100) : toMonthly(st.unit, st.value, input.rentableSf);
    if (month <= st.toMonth) return rent;
  }
  // Past the last step: one increase every 12 months, the first right after the last
  // step ends (with no steps, on each lease anniversary as usual).
  const lastEnd = esc.steps.length ? esc.steps[esc.steps.length - 1]!.toMonth : 0;
  const bumps = lastEnd > 0 ? Math.ceil((month - lastEnd) / 12) : Math.ceil(month / 12) - 1;
  return rent * Math.pow(1 + (esc.thereafterPct ?? 0) / 100, bumps);
}

export function analyzeLease(input: LeaseInput): LeaseAnalysis {
  if (!Number.isInteger(input.termMonths) || input.termMonths <= 0) {
    throw new CommissionInputError(`Term must be a whole number of months (got ${input.termMonths}).`);
  }
  requirePositive(input.baseRent.value, 'Starting base rent');
  const abatements = input.abatements ?? [];
  for (const a of abatements) {
    if (!Number.isInteger(a.startMonth) || a.startMonth < 1 || !Number.isInteger(a.months) || a.months < 1) {
      throw new CommissionInputError('Each free-rent period needs a start month and a number of months (both 1 or more).');
    }
    if (!(a.pct > 0 && a.pct <= 100)) {
      throw new CommissionInputError(`Free-rent % must be between 0 and 100 (got ${a.pct}).`);
    }
  }

  const opexEnabled = input.opex?.enabled === true;
  if (opexEnabled && !(Number(input.opex?.psfYear) >= 0)) {
    throw new CommissionInputError('Paid on OPEX is on, so an estimated OPEX $/SF/yr is required.');
  }

  const schedule: LeaseMonth[] = [];
  for (let month = 1; month <= input.termMonths; month++) {
    const year = Math.ceil(month / 12);
    const baseRent = monthlyBaseRent(input, month);
    const abatedPct = Math.min(
      100,
      abatements
        .filter((a) => month >= a.startMonth && month < a.startMonth + a.months)
        .reduce((total, a) => total + a.pct, 0),
    );
    schedule.push({ month, year, baseRent, abatedPct, abatedAmount: (baseRent * abatedPct) / 100 });
  }

  const aggregateBaseRentUnabated = schedule.reduce((t, m) => t + m.baseRent, 0);
  const abatedRent = schedule.reduce((t, m) => t + m.abatedAmount, 0);
  const aggregateBaseRent = aggregateBaseRentUnabated - abatedRent;

  const monthlyOpex = opexEnabled ? (Number(input.opex?.psfYear) * input.rentableSf) / 12 : 0;
  const aggregateOpex = monthlyOpex * input.termMonths;

  const firstFull = schedule.find((m) => m.abatedPct === 0) ?? null;
  const startingMonthlyRent = monthlyBaseRent(input, 1);

  return {
    rentableSf: input.rentableSf,
    termMonths: input.termMonths,
    startingMonthlyRent,
    startingRentPsfYear: input.rentableSf > 0 ? (startingMonthlyRent * 12) / input.rentableSf : null,
    aggregateBaseRentUnabated,
    abatedRent,
    aggregateBaseRent,
    aggregateOpex,
    aggregateLeaseValue: aggregateBaseRent + aggregateOpex,
    firstFullMonth: firstFull?.month ?? null,
    firstFullMonthBaseRent: firstFull?.baseRent ?? null,
    monthlyOpex,
    commissionMonthlyRent: firstFull ? firstFull.baseRent + monthlyOpex : null,
    opexEnabled,
    schedule,
  };
}
